import moment from 'moment';

function settingsOf(planSettings) {
  const settings = planSettings || {};
  const general = settings.general || {};
  const tiss = settings.tiss || {};
  return {
    tiss: !!tiss.enabled,
    record: general.record || {},
    sadt: tiss.sadt || {},
    consultation: tiss.consultation || {},
    providerReferral: general.providerReferral || {},
  };
}

export default function checklist(account, planSettings = {}) {
  const {
    tiss, record, sadt, consultation, providerReferral,
  } = settingsOf(planSettings);
  const authorization = sadt.authorization || {};
  const checks = [];
  const push = (code, ok, label, level, message, field) => checks.push({
    code, ok, label, level: ok ? null : level, message: ok ? label : message, field,
  });

  const items = account.items || [];
  const insured = account.insurance && account.insurance.insured
    ? account.insurance.insured
    : {};
  const service = account.service || {};
  const auth = account.authorization || {};
  const isSadt = account.type === 'sadt';
  const isConsultation = account.type === 'consultation';
  const isReturn = service.consultationType === 'return'
    || (items.length > 0 && items.every(item => item.return));
  const recordRequired = !isReturn
    && !!(record.required || (record.digits && record.digits.enabled));

  if (isConsultation) {
    push('noItems', items.length > 0, 'Consulta selecionada', 'block', 'Selecione a consulta', 'items');
    const single = items.length <= 1 && items.every(item => item.expense.type === 'consultation');
    push(
      'consultationSingleItem',
      single,
      'Um único serviço de consulta',
      'block',
      items.length > 1 ? 'Guia de consulta com mais de um serviço' : 'O serviço da guia não é consulta',
      'items',
    );
  } else {
    push('noItems', items.length > 0, 'Ao menos um serviço', 'block', 'Guia sem serviços', 'items');
  }

  if (isSadt && items.filter(item => item.expense.type === 'consultation').length > 1) {
    push(
      'sadtSingleConsultation',
      false,
      'No máximo uma consulta',
      'block',
      'Guia SP/SADT com mais de uma consulta',
      'items',
    );
  }

  const unpriced = items.filter(item => !item.return && !(item.price && item.price.priced));
  push(
    'notPriced',
    unpriced.length === 0,
    `Itens com preço (${items.length - unpriced.length} de ${items.length})`,
    'block',
    unpriced.length === 1
      ? `Serviço '${unpriced[0].expense.name}' sem preço no convênio`
      : `${unpriced.length} serviços sem preço no convênio`,
    'items',
  );

  push(
    'providerReferral',
    !!(account.referral && account.referral.provider),
    'Nº guia prestador',
    'block',
    'Sem nº guia prestador',
    'referral.provider',
  );

  const byMode = providerReferral.mode === 'insurance_referral';
  if (isConsultation && (consultation.requiredInsuranceReferral || byMode)) {
    push(
      'insuranceReferral',
      !!(account.referral && account.referral.insurance),
      'Nº guia operadora',
      'block',
      'Sem nº guia operadora',
      'referral.insurance',
    );
  }

  if (isSadt && (authorization.requiredInsuranceReferral || byMode)) {
    push(
      'insuranceReferral',
      !!(account.referral && account.referral.insurance),
      'Nº guia operadora',
      'block',
      'Sem nº guia operadora',
      'referral.insurance',
    );
  }

  if (isSadt && authorization.requiredMainReferral) {
    push(
      'mainReferral',
      !!(account.referral && account.referral.main),
      'Nº guia principal',
      'block',
      'Sem nº guia principal',
      'referral.main',
    );
  }

  if (isSadt && sadt.requiredRequestDate) {
    push(
      'requestDate',
      !!(account.request && account.request.date),
      'Data da solicitação',
      'block',
      'Sem data da solicitação',
      'request.date',
    );
  }

  if (isSadt && authorization.requiredDate) {
    push('authorizationDate', !!auth.date, 'Data da autorização', 'block', 'Sem data da autorização', 'authorization.date');
  }

  if (isSadt && authorization.requiredValidity) {
    push(
      'authorizationValidity',
      !!auth.validity,
      'Validade da autorização',
      'block',
      'Sem validade da autorização',
      'authorization.validity',
    );
  }

  if (recordRequired) {
    push('missingRecord', !!insured.record, 'Carteirinha', 'block', 'Sem carteirinha', 'insured.record');
  }

  if (record.digits && record.digits.enabled && insured.record) {
    const { length } = String(insured.record);
    const min = Number(record.digits.min) || 0;
    const max = Number(record.digits.max) || 0;
    const ok = length >= min && (!max || length <= max);
    push(
      'recordDigits',
      ok,
      'Dígitos da carteirinha',
      'block',
      length < min ? `Carteirinha com menos de ${min} dígitos` : `Carteirinha com mais de ${max} dígitos`,
      'insured.record',
    );
  }

  if (insured.validity || (!isReturn && record.requiredValidity)) {
    const expired = !!(insured.validity && account.startDate
      && moment(insured.validity).isBefore(moment(account.startDate), 'day'));
    const missing = !isReturn && record.requiredValidity && !insured.validity;
    let message = 'Validade da carteirinha';
    if (expired) message = `Carteirinha vencida em ${moment(insured.validity).format('DD/MM/YYYY')}`;
    if (missing) message = 'Sem validade da carteirinha';
    push(
      'recordExpired',
      !expired && !missing,
      'Validade da carteirinha',
      'block',
      message,
      'insured.validity',
    );
  }

  if (tiss) {
    const missingCbo = items.filter(item => !(item.professional
      && item.professional.specialty && item.professional.specialty.code));
    push(
      'missingCbo',
      missingCbo.length === 0,
      'CBO do executante',
      'block',
      missingCbo.length === 1
        ? `Sem CBO do executante '${missingCbo[0].professional ? missingCbo[0].professional.name : ''}'`
        : `${missingCbo.length} itens sem CBO do executante`,
      'items',
    );
  }

  if (!isConsultation) {
    const missingWayTechnique = items.filter(item => item.rules && item.rules.requireWayTechnique
      && !(item.way && item.technique));
    if (missingWayTechnique.length > 0) {
      push(
        'missingWayTechnique',
        false,
        'Via de acesso e técnica',
        'block',
        missingWayTechnique.length === 1
          ? `Sem via de acesso ou técnica em '${missingWayTechnique[0].expense.name}'`
          : `${missingWayTechnique.length} procedimentos sem via de acesso ou técnica`,
        'items',
      );
    }
  }

  if (isSadt) {
    if (tiss) {
      push(
        'missingRequester',
        !!(account.request && account.request.professional
          && account.request.professional.name),
        'Solicitante (15–19)',
        'block',
        'Sem profissional solicitante',
        'request.professional.name',
      );
    }
    if (authorization.requiredPassword) {
      push(
        'missingPassword',
        !!auth.password,
        'Senha de autorização',
        'block',
        'Sem senha de autorização',
        'authorization.password',
      );
    }
    if (tiss) {
      push('missingServiceType', !!service.type, 'Tipo de atendimento', 'block', 'Sem tipo de atendimento', 'service.type');
      push('missingCharacter', !!service.character, 'Caráter do atendimento', 'block', 'Sem caráter do atendimento', 'service.character');
      const missingDegree = items.filter(item => item.kind !== 'other_expense'
        && !(item.professional && item.professional.degreeParticipation));
      push(
        'missingDegree',
        missingDegree.length === 0,
        'Grau de participação dos procedimentos',
        'block',
        missingDegree.length === 1
          ? `Sem grau de participação em '${missingDegree[0].expense.name}'`
          : `${missingDegree.length} procedimentos sem grau de participação`,
        'items',
      );
    }
  }

  if (isConsultation && tiss) {
    push(
      'missingConsultationType',
      !!service.consultationType,
      'Tipo de consulta',
      'block',
      'Sem tipo de consulta',
      'service.consultationType',
    );
  }

  if (tiss && !(account.type === 'simple')) {
    push(
      'missingAccidentIndication',
      !!service.accidentIndication,
      'Indicação de acidente',
      'block',
      'Sem indicação de acidente',
      'service.accidentIndication',
    );
  }

  if (auth.validity && !isConsultation) {
    const days = moment(auth.validity).startOf('day').diff(moment().startOf('day'), 'days');
    const soon = days >= 0 && days <= 3;
    push(
      'passwordExpiring',
      !soon,
      'Validade da senha',
      'warn',
      `Senha vence em ${days} dia(s)`,
      'authorization.validity',
    );
  }

  const issues = checks.filter(check => !check.ok);
  return {
    checks,
    issues,
    ready: !issues.some(issue => issue.level === 'block'),
  };
}
